import { useEffect, useState } from 'react';

import { Delete02Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { emailFieldError } from 'helpers/validators/pattern-validators';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { Button, Email, Input, InputGroup, PhoneNumberPicker } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';

import { selectCompany } from 'api/company-slice';

const EMAIL_COMPARE_ERROR_MESSAGE =
  'This email address belongs to the candidate or another referee. Please use a different email address.';
const CONTACT_COMPARE_ERROR_MESSAGE =
  'This contact number belongs to the candidate or another referee. Please use a different contact number.';

export const RefereeDetailsForm = ({
  referee,
  candidate,
  referees = [],
  onUpdate = () => {},
  deleteReferee = () => {},
  onClose = () => {},
}) => {
  const company = useSelector(selectCompany);

  const {
    register,
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id: referee.id,
      first_name: referee.first_name,
      last_name: referee.last_name,
      email: referee.email,
      contact_number: referee.contact_number,
      company_name: referee.company_name,
      role: referee.role,
      working_period: referee.working_period,
      form_name: referee.form_name,
    },
  });

  const otherActiveReferees = referees.filter(
    other => other.id !== referee.id && !other.replaced_by && !other.deleted && !other.canceled,
  );
  const compareEmails = [candidate?.email, ...otherActiveReferees.map(other => other.email)].filter(
    Boolean,
  );
  const compareContacts = [
    candidate?.contact_number,
    ...otherActiveReferees.map(other => other.contact_number),
  ].filter(Boolean);

  const [showDeletionAlert, setShowDeletionAlert] = useState(false);

  // Reset candidate details
  useEffect(
    () =>
      reset({
        id: referee.id,
        first_name: referee.first_name,
        last_name: referee.last_name,
        email: referee.email,
        contact_number: referee.contact_number,
        company_name: referee.company_name,
        role: referee.role,
        working_period: referee.working_period,
        form_name: referee.form_name,
      }),
    [referee],
  );

  const onSubmit = async data => {
    onUpdate(data, !!referee.completed_at);
  };

  // Delete referee
  const handleDeletion = async () => {
    setShowDeletionAlert(false);
    onClose();
    deleteReferee(referee.id);
  };

  return (
    <div className={cn('card', 'card-with-border')}>
      <Alert
        show={showDeletionAlert}
        title={`Are you sure you want to delete ${referee?.first_name}?`}
        message="Referee deletion cannot be undone. Once deleted, we will adjust the number of referees for this candidate"
        ok="Delete"
        type="error"
        icon={Delete02Icon}
        onOk={handleDeletion}
        showConfirmationInput
        cancel="Cancel"
        onCancel={() => setShowDeletionAlert(false)}
      />
      <div className={cn('u-padding', 'd-flex', 'justify-content-between', 'align-items-center')}>
        <div>
          <div className="t-small">REFEREE</div>
          <div className="title-2">{referee.name}</div>
        </div>
        <IconButton
          icon="trash-2"
          tip="Delete"
          className="u-margin-right"
          onClick={() => setShowDeletionAlert(true)}
        />
      </div>
      <hr className="divider u-margin-0" />
      <form className="u-padding" onSubmit={handleSubmit(onSubmit)}>
        <div className="inline u-margin-top">
          <InputGroup title="First name">
            <Input
              name="first_name"
              register={register}
              validators={{ required: true }}
              disabled={!!referee.completed_at}
              placeholder="John"
            />
          </InputGroup>
          <InputGroup title="Last name">
            <Input
              name="last_name"
              register={register}
              validators={{ required: true }}
              disabled={!!referee.completed_at}
              placeholder="Doe"
            />
          </InputGroup>
        </div>
        <InputGroup title="Email">
          <Email
            name="email"
            register={register}
            validators={{ required: true }}
            placeholder="john@checkmate.com"
            compareEmailValidation
            compareEmail={compareEmails}
            compareErrorMessage={EMAIL_COMPARE_ERROR_MESSAGE}
            error={emailFieldError(errors?.email)}
          />
        </InputGroup>
        <InputGroup title="Form">
          <Input name="form_name" register={register} disabled={true} />
        </InputGroup>
        <InputGroup title="Contact Number">
          <Controller
            control={control}
            name="contact_number"
            rules={{
              validate: value => !compareContacts.includes(value) || CONTACT_COMPARE_ERROR_MESSAGE,
            }}
            render={({ field: { onChange, value } }) => (
              <PhoneNumberPicker
                value={value}
                onChange={onChange}
                disabled={!!referee.completed_at}
                defaultCountry={company?.country}
                error={errors?.contact_number}
              />
            )}
          />
        </InputGroup>
        <InputGroup title="Where did referee and candidate work together?">
          <Input
            name="company_name"
            register={register}
            placeholder="Checkmate"
            disabled={!!referee.completed_at}
          />
        </InputGroup>
        <InputGroup title="Referee relationship with candidate">
          <Input
            name="role"
            register={register}
            placeholder="Manager"
            disabled={!!referee.completed_at}
          />
        </InputGroup>
        <InputGroup title="Relationship length">
          <Input
            name="working_period"
            register={register}
            placeholder="2 years"
            disabled={!!referee.completed_at}
          />
        </InputGroup>

        <Button className="u-width-100" submit>
          Update referee
        </Button>
      </form>
    </div>
  );
};
